import { makeAutoObservable } from "mobx";
import { get_openOrder } from "api/v4/order";
import { get_accountOverview } from "api/old/xt-lever";
import { TradeOrderStateEnum, TradeSideEnum, TradeTypeEnum } from "store/trade";
import StoreMarket, { TypeEnum } from "store/market";

export const ConvertCurrencyAry = [
  { key: "usd", note: "$", maxPrecision: 2 },
  { key: "btc", note: "฿", maxPrecision: 8 },
  { key: "inr", note: "₹", maxPrecision: 2 },
  { key: "idr", note: "Rp", maxPrecision: 2 },
  { key: "cny", note: "￥", maxPrecision: 2 },
] as const;

export type ConvertCurrencyType = (typeof ConvertCurrencyAry)[number]["key"];

export interface BalancesProps {
  currency: string;
  currencyId?: number;
  frozenAmount?: string;
  availableAmount: string;
  totalAmount?: string;
  convertBtcAmount?: string;
  convertUsdtAmount?: string;
}

export interface BalancesLeverCurrencyProps {
  currency: string;
  currencyId: number;
  frozenAmount: string;
  availableAmount: string;
  totalAmount: string;
  loanAmount: string;
  capitalAmount: string;
  interestAmount: string;
  updatedTime: number;
}

export interface BalancesLeverProps {
  symbol: string;
  symbolId: number;
  btcNetAmount: string;
  btcLoanAmount: string;
  usdtNetAmount: string;
  usdtLoanAmount: string;
  base: BalancesLeverCurrencyProps;
  quote: BalancesLeverCurrencyProps;
}

export enum AccountEnum {
  spot = "SPOT",
  lever = "LEVER",
  futures_u = "FUTURES_U",
  futures_c = "FUTURES_C",
  finance = "FINANCE",
}

interface LeverAccountOverviewCoinProps {
  tradeFreezeAmount: string;
  tradeAmount: string;
  hasLoanAmount: string;
  interestAmount: string;
}

interface LeverAccountOverviewProps {
  marketName: string;
  liquidationPrice: string;
  sellCoin: LeverAccountOverviewCoinProps;
  buyCoin: LeverAccountOverviewCoinProps;
}

export interface OpenOrderProps {
  symbol: string;
  orderId: string;
  time: number;
  updatedTime: number;
  type: TradeTypeEnum;
  side: TradeSideEnum;
  price: string;
  origQty: string;
  origQuoteQty: string;
  tradeBase: string;
}

export interface WsOrderProps {
  s: string;
  i: string;
  t: number;
  ct: number;
  tp: TradeTypeEnum;
  sd: TradeSideEnum;
  p: string;
  oq: string;
  oqq: string;
  eq: string;
  st: TradeOrderStateEnum;
  bt: TypeEnum;
  lq: string;
  ci: string;
  ap: string;
  f: string;
}

export interface WsBalanceProps {
  z: string;
  s: string;
  c: string;
  b: string;
  f: string;
  a: string;
  t: number;
}

interface StateProps {
  convertCurrency: ConvertCurrencyType;
  currencyQuantity: WithUndefined<BalancesProps | null>;
  currencyPrice: WithUndefined<BalancesProps | null>;
  leverAccountOverview: WithUndefined<LeverAccountOverviewProps>;
  openOrder: WithUndefined<OpenOrderProps[]>;
  wsOrder: WithUndefined<WsOrderProps>;
  wsBalance: WithUndefined<WsBalanceProps>;
}

let cancelFunGetOpenOrder;

const balances = makeAutoObservable(
  {
    convertCurrency: "usd" as StateProps["convertCurrency"],
    currencyQuantity: undefined as StateProps["currencyQuantity"],
    currencyPrice: undefined as StateProps["currencyPrice"],
    leverAccountOverview: undefined as StateProps["leverAccountOverview"],
    openOrder: undefined as StateProps["openOrder"],
    wsOrder: undefined as StateProps["wsOrder"],
    wsBalance: undefined as StateProps["wsBalance"],

    updateState(payload: Partial<StateProps>) {
      for (const key in payload) {
        this[key] = payload[key];
      }
    },

    getAccountOverview(marketName: string) {
      get_accountOverview(marketName).then((data: StateProps["leverAccountOverview"]) => {
        this.leverAccountOverview = data;
      });
    },

    getOpenOrder(config?) {
      try {
        cancelFunGetOpenOrder && cancelFunGetOpenOrder();
      } catch (e) {
        cancelFunGetOpenOrder = null;
      }

      return get_openOrder({
        ...(config || {
          params: {
            bizType: StoreMarket.type,
          },
        }),
        cancelFun: (c) => (cancelFunGetOpenOrder = c),
      })
        .then((data) => {
          this.openOrder = data;
        })
        .catch(() => {})
        .finally(() => {
          cancelFunGetOpenOrder = null;
        });
    },
  },
  {},
  {
    autoBind: true,
    deep: false,
  }
);

export default balances;
